import * as THREE from 'three';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { appendModelVersion, shouldVersionModelUrl } from './modelHotReload.js';

// Centralized asset loader with a LoadingManager for progress UI.

export class AssetLoader {
  constructor({ modelVersionToken = '' } = {}) {
    this.manager = new THREE.LoadingManager();
    this.manager.onProgress = (url, loaded, total) => {
      const p = total > 0 ? loaded / total : 0;
      this.onProgress?.(p, url);
    };
    this.manager.onError = (url) => {
      this.onError?.(url);
    };

    this.fbxLoader = new FBXLoader(this.manager);
    this.gltfLoader = new GLTFLoader(this.manager);
    this.textureLoader = new THREE.TextureLoader(this.manager);

    this.cache = new Map();
    this.onProgress = null;
    this.onError = null;
    this.modelVersionToken = modelVersionToken;
  }

  _resolveUrl(url, { versionModels = true } = {}) {
    if (versionModels && shouldVersionModelUrl(url)) {
      return appendModelVersion(url, this.modelVersionToken);
    }
    return url;
  }

  _normalizeAssetUrl(url) {
    try {
      const baseOrigin = typeof window !== 'undefined' ? window.location.origin : 'https://localhost';
      const u = new URL(url, baseOrigin);
      // Decode once, then encode once to avoid accidental "%2520" double-encoding.
      const normalizedPath = decodeURIComponent(u.pathname)
        .split('/')
        .map((seg) => encodeURIComponent(seg))
        .join('/');
      return `${normalizedPath}${u.search}${u.hash}`;
    } catch (_err) {
      return url;
    }
  }

  async loadFBX(url, { optional = true, versionModels = true } = {}) {
    const resolvedUrl = this._resolveUrl(url, { versionModels });
    if (this.cache.has(resolvedUrl)) return this.cache.get(resolvedUrl);
    const requestUrl = this._normalizeAssetUrl(resolvedUrl);
    try {
      const obj = await new Promise((resolve, reject) => {
        this.fbxLoader.load(requestUrl, resolve, undefined, reject);
      });
      this.cache.set(resolvedUrl, obj);
      return obj;
    } catch (err) {
      if (optional) {
        console.warn(`[loader] optional FBX failed: ${requestUrl}`, err);
        return null;
      }
      throw err;
    }
  }

  async loadGLTF(url, { optional = true, versionModels = true } = {}) {
    const resolvedUrl = this._resolveUrl(url, { versionModels });
    if (this.cache.has(resolvedUrl)) return this.cache.get(resolvedUrl);
    try {
      const gltf = await new Promise((resolve, reject) => {
        this.gltfLoader.load(resolvedUrl, resolve, undefined, reject);
      });
      this.cache.set(resolvedUrl, gltf);
      return gltf;
    } catch (err) {
      if (optional) {
        console.warn(`[loader] optional GLTF not found: ${resolvedUrl}`);
        return null;
      }
      throw err;
    }
  }
}

// Traverse a model and apply shadow flags + material fixups.
export function prepareModel(root) {
  if (!root) return root;
  root.traverse((child) => {
    if (child.isMesh) {
      child.castShadow = true;
      child.receiveShadow = true;
      // Imported skinned rigs can report unstable bounds and vanish from view.
      child.frustumCulled = false;
      if (child.material) {
        const mats = Array.isArray(child.material) ? child.material : [child.material];
        for (const m of mats) {
          if (m.map) m.map.colorSpace = THREE.SRGBColorSpace;
          // FBX exports can carry unexpected alpha/side flags that make meshes
          // appear invisible in-game. Normalize to robust opaque defaults.
          m.transparent = false;
          m.opacity = 1;
          m.alphaTest = 0;
          m.depthWrite = true;
          m.side = THREE.DoubleSide;
          // Emissive self-illumination based on surface brightness
          const c = m.color;
          if (c) {
            const brightness = c.r * 0.299 + c.g * 0.587 + c.b * 0.114;
            if (brightness > 0.7) {
              // Light surfaces (floors, light walls) — subtle teal glow
              m.emissive = new THREE.Color(0x0a2020);
              m.emissiveIntensity = 0.35;
            } else if (brightness > 0.3) {
              // Mid-tone surfaces (props, columns) — faint blue-grey
              m.emissive = new THREE.Color(0x060a12);
              m.emissiveIntensity = 0.25;
            } else {
              // Dark surfaces (walls, ceilings) — minimal hint only
              m.emissive = new THREE.Color(0x030408);
              m.emissiveIntensity = 0.1;
            }
          }
          m.needsUpdate = true;
        }
      }
    }
  });
  return root;
}

// Deep clone an FBX scene with SkinnedMesh skeletons intact.
// (Three's SkeletonUtils is the recommended path.)
export async function cloneSkinned(root) {
  const { SkeletonUtils } = await import('three/examples/jsm/utils/SkeletonUtils.js');
  return SkeletonUtils.clone(root);
}

// Adds a ceiling spotlight to illuminate a single room tile.
// Call once per room after placing level geometry.
// x, z = the world-space center of the room.
export function addRoomSpot(scene, x, z) {
  const spot = new THREE.SpotLight(0x88ffee, 5.0);
  spot.position.set(x, 9, z);
  spot.target.position.set(x, 0, z);
  spot.angle = Math.PI / 5.5;
  spot.penumbra = 0.6;
  spot.decay = 2;
  spot.distance = 22;
  spot.castShadow = false;
  scene.add(spot);
  scene.add(spot.target);
}
